import React, { useMemo } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { Typography } from '@cp/ds/src/components/typography';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { CITY_PATH_PARAMETER, usePathGenerator } from '@cp/entities/clientWebRoutes';
import { FeedbackViewModel } from '@common/model/catalog/feedback/view';
import { coreCatalogItemApi, formatCatalogItemDate } from '@cp/entities/catalogItem';
import { Image as Img } from '@youhelp-ru/ui';
import { ClientPhotoSize } from '@common/const/client/photo';
import { getResizedAssetItem, getResizedAssetItems } from '@cp/shared/image';
import { ClientPhotoViewModel } from '@common/model/client/photo/view';
import { ImageAssetItem } from '@common/model/resource/image';
import { ResumeAuthorName } from '@cp/entities/resume';
import { useAuthorAge } from '@cp/entities/resume/model';
import { feedbackSign } from '@common/model/catalog/feedback';
import { RoutedLink } from 'src/shared/ui/link';
import { Achievement } from 'src/entities/resume/ui/card/achievement';
import { generateCatalogItemPermalink } from 'src/entities/catalog';

type FeedbackCardType = {
  feedback: FeedbackViewModel;
  loadPolicy: 'lazy' | 'eager';
  withBorder?: boolean;
};

const mainPhotoToAsset = (photo: ClientPhotoViewModel, size: number): ImageAssetItem => {
  const resizedAsset = getResizedAssetItem(photo.image.base, size);
  return resizedAsset || { src: photo.notCroppedOriginalSrc as string, width: size, height: size };
};

export const FeedbackCard = ({ feedback, loadPolicy, withBorder }: FeedbackCardType) => {
  const theme = useTheme();
  const generatePath = usePathGenerator();

  const { rating, createdAt, review, subjectCatalogItemId, authorName } = feedback;

  const createdDate = formatCatalogItemDate(new Date(createdAt), true);

  const { data } = coreCatalogItemApi.endpoints.getById.useQuery({ id: subjectCatalogItemId || 0 });

  const href = data ? generateCatalogItemPermalink(data, generatePath, { CITY_PATH_PARAMETER }) : undefined;
  const permalink = href ? String(href) : undefined;

  const clientProfile = data?.clientProfile;

  const { mainAsset, assets } = useMemo(() => {
    const clientPhoto = clientProfile?.mainPhoto;
    if (!clientPhoto) {
      return { assets: [] as ReturnType<typeof getResizedAssetItems>, mainAsset: undefined };
    }
    return {
      assets: getResizedAssetItems(clientPhoto.image, ClientPhotoSize.Small),
      mainAsset: mainPhotoToAsset(clientPhoto, ClientPhotoSize.Small),
    };
  }, [clientProfile?.mainPhoto]);

  const formattedAge = useAuthorAge(clientProfile?.age);

  return (
    <RoutedLink
      disableHover
      external={Boolean(permalink)}
      href={permalink}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 1.5, md: 2.5 },
        borderRadius: '24px',
        border: withBorder ? `1px solid ${theme.palette['gray middle']}` : 'none',
        gap: '12px',
        maxWidth: '343px',
        height: '100%',
        backgroundColor: 'white',
        overflow: 'hidden',
      }}
      underline={false}
    >
      <Stack direction="row" sx={{ gap: '12px' }}>
        <Box sx={{ maxWidth: '108px', width: '100%', height: '108px', borderRadius: '12px', overflow: 'hidden' }}>
          <picture>
            {assets?.map((asset) => (
              <source key={asset.src} srcSet={asset.src} type={asset.mimeType} />
            ))}
            <Img
              alt={[clientProfile?.name, clientProfile?.surname].filter(Boolean).join(' ')}
              height={'100%'}
              loading={loadPolicy}
              src={mainAsset?.src || ''}
              sx={{ display: 'block', objectFit: 'cover' }}
              width={'100%'}
            />
          </picture>
        </Box>
        <Stack sx={{ gap: '8px', width: '100%' }}>
          <Stack alignItems="center" direction="row" sx={{ flexWrap: 'wrap', columnGap: '2px' }}>
            <ResumeAuthorName
              author={clientProfile || {}}
              sx={{ fontSize: { xs: '16px', md: '18px' }, fontWeight: { sx: 500, md: 700 } }}
            />
            {','}
            <Typography
              component="span"
              sx={{ typography: { xs: 'small text bold', md: 'caption bold' }, pt: '3px', color: theme.palette.text.primary }}
              variant="caption bold"
            >
              {formattedAge}
            </Typography>
          </Stack>

          <Stack direction="row" sx={{ gap: '8px', flexWrap: 'wrap', width: '100%', minWidth: '103px' }}>
            {clientProfile?.isOnline && <Achievement type="online" />}
            {clientProfile?.trainingCompletedAt && <Achievement type="school" />}
            {clientProfile?.isDocumentsChecked && <Achievement type="verified" />}
          </Stack>
        </Stack>
      </Stack>
      <Stack sx={{ height: { xs: '204px', md: '234px' }, gap: '8px' }}>
        <Typography
          component="p"
          sx={{ m: 0, overflow: 'hidden', typography: { xs: 'caption', md: 'body' }, lineClamp: 7 }}
          variant="caption"
        >
          {`${feedbackSign({ rating })} `}
          {review?.text}
        </Typography>
        <Stack alignItems="flex-end" direction="row" sx={{ gap: '3px', flexWrap: 'wrap' }}>
          <Typography
            component="span"
            sx={{
              color: theme.palette.text.secondary,
            }}
            variant="caption"
          >
            {`${authorName},`}
          </Typography>
          <Typography
            component="span"
            sx={{
              color: theme.palette.text.secondary,
            }}
            variant="caption"
          >
            {createdDate}
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          typography: 'body bold',
          color: theme.palette['brand pink'],
          cursor: 'pointer',
          '&:active, &:focus': { outline: 'none' },
        }}
      >
        Узнать больше
      </Box>
    </RoutedLink>
  );
};
